/**
 * Seeded RNG streams for audio-side variation (plan 24 Phase 0 contract).
 *
 * The sim is RNG-free by contract (plan 04); every audio variation source (music form
 * operators, SFX variation banks) draws from a stream created here instead — so sim
 * determinism and measured runs are untouched, while tests and offline renders pin exact
 * seeds for reproducible audio behaviour.
 *
 * mulberry32: tiny, fast, good-enough distribution for musical variation. 0 asset bytes.
 */

/** A deterministic stream of floats in [0, 1). */
export type Rng = () => number;

/** The contract factory: same seed → same sequence, forever. */
export function makeRng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Per-attempt seed for PLAY (plan 24 Phase 0 item 2): a fresh time-derived seed per
 * director construction — every restart varies anew (the dossier §2 Verification seed
 * policy; restart compounding §1.1 is addressed by variety). Tests and renders pass an
 * explicit seed instead and never call this.
 */
export function runSeed(): number {
  return (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
}
